import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Check } from 'lucide-react';
import { capitalize } from 'lodash';
import { useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { IconBeneficiaryH, IconDepositorH } from 'src/components/Svg/Icons';
import { Table, TableBody, TableCell, TableRow, TableFooter, TableHeader, TableHead } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { OrganizationRegistrationNumberTooltip } from '@/components/Organization';
import { setOrganizationFullAddress } from 'src/utils/escrow';
import { cn } from 'src/lib/utils';
import { AgreementFormEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';

import { useParty } from '../model';

type IRepresentativeRow = {
  name: string;
  email: string;
  phone: string;
  signatory: boolean;
};

const columnHelper = createColumnHelper<IRepresentativeRow>();
const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (props) => <div className="text-sm">{props.getValue()}</div>,
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: (props) => <div className="text-sm">{props.getValue()}</div>,
  }),
  columnHelper.accessor('phone', {
    header: 'Phone number',
    cell: (props) => <div className="text-sm">{props.getValue() || '-'}</div>,
  }),
  columnHelper.accessor('signatory', {
    header: 'Signatory',
    cell: (props) =>
      props.getValue() ? (
        <span className="mx-auto size-3.5 bg-secondary-foreground rounded-full flex items-center justify-center">
          <Check className="size-3 text-white" />
        </span>
      ) : null,
  }),
];

const RepresentativesTable = () => {
  const { type, party, showSignatories, onEdit } = useParty();

  const data = useMemo(
    () =>
      party?.contacts.map((contact) => ({
        name: contact.name,
        email: contact.email,
        phone: contact.phone || '-',
        signatory: contact.signatory,
      })) || [],
    [party],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility: {
        signatory: showSignatories,
      },
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="text-base font-bold text-foreground">
        {capitalize(type)} representatives{showSignatories && ' and signatories'}
      </div>
      <div className="rounded-md border border-border grow-1 overflow-hidden h-full">
        <Table>
          <TableHeader>
            <TableRow>
              {table.getFlatHeaders().map((header) => (
                <TableHead key={header.id} className={cn(header.id === 'signatory' && 'w-[96px] text-center')}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={4}>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={onEdit}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const OrganizationDetailsTable = () => {
  const { type, onEdit, party } = useParty();

  const address = {
    street: party?.street || '',
    streetNumber: party?.streetNumber || '',
    city: party?.city || '',
    postalCode: party?.postalCode || '',
    region: party?.region || '',
    country: party?.country || '',
  };

  const rows = [
    {
      id: 'organization-name',
      label: 'Organization Name',
      value: party?.companyName,
    },
    {
      id: 'website',
      label: 'Website',
      value: party?.companyWebsite || '-',
    },
    {
      id: 'registration-number',
      label: (
        <div className="flex items-center gap-1">
          Registration Number <OrganizationRegistrationNumberTooltip />
        </div>
      ),
      value: party?.companyRegistrationNumber || '-',
    },
    {
      id: 'address',
      label: 'Address',
      value: setOrganizationFullAddress(address),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="text-base font-bold text-foreground">{capitalize(type)} organization details</div>
      <div className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableBody>
            {rows.map(({ id, label, value }) => (
              <TableRow key={id}>
                <TableCell className="w-[195px] font-medium">{label}</TableCell>
                <TableCell className="w-[80px]">:</TableCell>
                <TableCell>
                  <div className="min-w-[190px]">{value}</div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3}>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={onEdit}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const partyIconMap = {
  [EscrowPartyTypeEnum.Depositor]: IconDepositorH,
  [EscrowPartyTypeEnum.Beneficiary]: IconBeneficiaryH,
};

const OrganizationCard = () => {
  const { type, agreementForm } = useParty();
  const Icon = partyIconMap[type];

  return (
    <Card>
      <CardHeader className="relative">
        <CardTitle className="flex items-center gap-2 text-xl">
          <Icon className="size-6" />
          {capitalize(type)} information
        </CardTitle>
        <CardDescription>
          {agreementForm === AgreementFormEnum.Bipartite && type === EscrowPartyTypeEnum.Beneficiary
            ? 'Organization details and key points of contact.'
            : 'Your organization details, key points of contact and signatories.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <OrganizationDetailsTable />
        <RepresentativesTable />
      </CardContent>
    </Card>
  );
};

export default OrganizationCard;
